import React, {useState, useEffect, useRef} from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {useDealContext} from "../../contexts/DealContext";
import {useUserContext} from "../../contexts/UserContext";
import {NumericFormat} from 'react-number-format';
import classnames from 'classnames';
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import {PaymentAdjustmentPopover} from "../../components/PaymentAdjustmentPopover";
import {AmountDueBreakdownPopover} from "../../components/AmountDueBreakdownPopover";

const formatNumber = (value, decimals = 2) =>
    Number.isFinite(Number(value))
        ? Number(value).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
        : '0.00'

export const DealPagePaymentSummary = () => {
    const {
        dealState, setDealStateWithUpdate, createUpdateFunc, updateDealResponse, isHistorical,
        submitPaymentAdjustment,
    } = useDealContext()
    const { canDesk } = useUserContext()

    const formattedQuote = dealState?.formatted_quote || {}

    // ── Lease "Monthly Payment" → Payment Adjustment ──
    // The field shows the computed payment and is the entry point: clicking or
    // focusing it opens the popover, where the new payment is typed. The quote
    // only changes if the popover calculates.
    const paymentFieldRef = useRef(null)
    const [adjustAnchor, setAdjustAnchor] = useState(null)
    const isReturningFocusRef = useRef(false)
    const wasAdjustingRef = useRef(false)
    const openPaymentAdjustment = () => {
        if (isHistorical || adjustAnchor || isReturningFocusRef.current) return
        setAdjustAnchor(paymentFieldRef.current)
    }
    // Once the popover is gone, hand focus back to the field without reopening it.
    useEffect(function returnFocusToPaymentField(){
        if (adjustAnchor) {
            wasAdjustingRef.current = true
            return
        }
        if (!wasAdjustingRef.current) return
        wasAdjustingRef.current = false
        isReturningFocusRef.current = true
        paymentFieldRef.current?.querySelector('input')?.focus()
        isReturningFocusRef.current = false
    }, [adjustAnchor])

    // ── Lease "Amount Due at Signing" → read-only breakdown ──
    // A re-price would leave the open breakdown stale, so it closes instead.
    const [breakdownAnchor, setBreakdownAnchor] = useState(null)
    const isRepricing = Boolean(updateDealResponse?.loading)
    useEffect(function closeBreakdownOnReprice(){
        if (isRepricing) setBreakdownAnchor(null)
    }, [isRepricing])

    // ── Editable "Tax Rate" ── Like the Residual override: typing is held here and
    // committed on Enter/blur. null = not being edited, so the quote's rate shows.
    const [taxRateDraft, setTaxRateDraft] = useState(null)

    if (!dealState) return null;

    // Calculate button — lives in the top (header) of the Payment section.
    // Behavior is unchanged (same createUpdateFunc + disabled/loading gating).
    const isCalcDisabled = !dealState.msrp || !dealState.cost
    const isCalcLoading = updateDealResponse?.loading && dealState._no_update !== true
    // Nothing to re-price on a delivered car, so the button is absent rather
    // than disabled — both render branches below read this one value. A role
    // that may not desk loses it for the same reason: the press would reach
    // MarketScan, which the backend refuses.
    const calculateButton = (isHistorical || !canDesk()) ? null : (
        <Tooltip title="Click or Press Enter to Calculate" arrow placement={'bottom'}>
            <button
                type="button"
                className={classnames('btn-calculate', { isDisabled: isCalcDisabled })}
                onClick={createUpdateFunc}
                disabled={isCalcDisabled || isCalcLoading}
            >
                {isCalcLoading ? 'Calculating...' : 'CALCULATE'}
                {!isCalcLoading && <KeyboardReturnIcon/>}
            </button>
        </Tooltip>
    )

    // Any typed value is a manual rate (red), even the automatic one; clearing it
    // returns to automatic taxes. Both layouts render this one input.
    const taxRateOverride = dealState.sales_tax_rate_override || ''
    const isTaxRateEdited = taxRateDraft !== null && taxRateDraft !== taxRateOverride
    const commitTaxRate = () => {
        if (isTaxRateEdited) setDealStateWithUpdate({ sales_tax_rate_override: taxRateDraft }, true)
        setTaxRateDraft(null)
    }
    const taxRateInput = (
        <div className={classnames('lender-input-wrap', { 'has-override': taxRateOverride })}>
            <span className={'prefix'}>%</span>
            <CustomCurrencyInput
                value={taxRateDraft ?? (taxRateOverride || formattedQuote.tax_rate)}
                disabled={isHistorical}
                allowBlank={true}
                allowNegative={false}
                decimalScale={4}
                isAllowed={({ floatValue }) => (floatValue ?? 0) < 100}
                onChange={(value, sourceInfo) => {
                    if (sourceInfo?.source === 'event') setTaxRateDraft(value)
                }}
                onBlur={commitTaxRate}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    // The commit re-prices; keep the page-level Enter from also re-pricing the old rate.
                    if (isTaxRateEdited) e.stopPropagation()
                    commitTaxRate()
                }}
                InputProps={{}}
            />
        </div>
    )

    const isPurchase = Boolean(dealState.is_purchase)
    // Monthly lease quotes only; PARTNER_API quotes have no worksheet data (theirs re-desks).
    const hasAmountDueBreakdown = !isPurchase && !dealState.is_one_pay && formattedQuote.source !== 'PARTNER_API'
    // Cash = purchase paid in full up front. Its Payment panel is a flat
    // breakdown (Selling Price + Taxes + Fees = Payment) rather than the
    // amortized lease/finance layout.
    const isCash = isPurchase && Boolean(dealState.is_one_pay)

    if (isCash) {
        const sumAmounts = (arr) => (arr || []).reduce((acc, x) => acc + (Number(x.amount) || 0), 0)
        const taxesTotal = sumAmounts(formattedQuote.taxes)
        const feesTotal = sumAmounts(formattedQuote.fees)
        const sellingPrice = dealState.selling_price || (Number(dealState.cost) + Number(dealState.profit))

        // The Payment total is a fixed, computed value — rendered as plain bold
        // text (not inside an input box), since cash isn't editable here.
        const amountRow = (label, value, emphasized = false) => (
            <div className={classnames('lender-row', { 'cash-payment-total': emphasized })}>
                <span className={'lender-key'}>{label}</span>
                <span className={'lender-val-plain'}>
                    <span className={'dollar'}>$</span>
                    <span className={'amount'}>
                        <NumericFormat
                            value={value || 0}
                            displayType={'text'}
                            thousandSeparator
                            decimalScale={2}
                        />
                    </span>
                </span>
            </div>
        )

        return (
            <Box className={'panel-payment'}>
                <div className={'panel-header'}>
                    <div className={'panel-title'}>Payment</div>
                    {calculateButton}
                </div>
                <div className={'panel-body'}>
                    {amountRow('Selling Price:', sellingPrice)}
                    {amountRow('Taxes:', taxesTotal)}
                    {amountRow('Fees:', feesTotal)}

                    <hr className={'payment-divider'}/>

                    {amountRow('Payment:', formattedQuote.monthly_payment, true)}

                    <div className={'lender-row'}>
                        <span className={'lender-key'}>Tax Rate:</span>
                        {taxRateInput}
                    </div>
                </div>
            </Box>
        )
    }

    return (
        <Box className={'panel-payment'}>
            <div className={'panel-header'}>
                <div className={'panel-title'}>Payment</div>
                {calculateButton}
            </div>
            <div className={'panel-body'}>
                {/* Amount Due at Signing — Lease only, blue amount. Monthly leases
                    show it in a read-only box (a button, so it can't be typed in)
                    that opens the breakdown. */}
                {
                    !isPurchase &&
                    <div className={'lender-row'}>
                        <span className={'lender-key'}>Amount Due at Signing:</span>
                        {
                            hasAmountDueBreakdown
                                ? <button
                                    type={'button'}
                                    className={'monthly-val-wrap amount-due-trigger'}
                                    aria-label={'Amount Due at Signing breakdown'}
                                    aria-haspopup={'dialog'}
                                    onClick={(e) => !isRepricing && setBreakdownAnchor(e.currentTarget)}
                                    // Enter opens the breakdown; it must not also re-run Calculate.
                                    onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                                  >
                                    <span className={'dollar'}>$</span>
                                    <span className={'amount payment-val-blue-text'}>
                                        {formatNumber(formattedQuote.amount_due_at_signing)}
                                    </span>
                                  </button>
                                : <span className={'lender-val-plain payment-val-blue'}>
                                    <span className={'dollar'}>$</span>
                                    <span className={'amount'}>{formatNumber(formattedQuote.amount_due_at_signing)}</span>
                                  </span>
                        }
                    </div>
                }

                {/* Base Payment */}
                <div className={'lender-row'}>
                    <span className={'lender-key'}>Base Payment:</span>
                    <span className={'lender-val-plain'}>
                        <span className={'dollar'}>$</span>
                        <span className={'amount'}>{formatNumber(formattedQuote.base_payment)}</span>
                    </span>
                </div>

                {/* Tax Rate */}
                <div className={'lender-row'}>
                    <span className={'lender-key'}>Tax Rate:</span>
                    {taxRateInput}
                </div>

                {/* Monthly Payment — editable for Lease only (typing sets
                    desired_payment so the backend solves the price to hit it).
                    Finance and one-pay deals render the value as plain text (no
                    input field), like a cash quote. One-pay deals read "One
                    Payment". */}
                <div className={'lender-row'}>
                    <span className={'lender-key'}>{dealState.is_one_pay ? 'One Payment:' : 'Monthly Payment:'}</span>
                    {
                        isPurchase
                            // Finance: plain text, no input box (like a cash quote).
                            ? <span className={'lender-val-plain'}>
                                <span className={'dollar'}>$</span>
                                <span className={'amount payment-val-blue-text'}>
                                    {formatNumber(formattedQuote.monthly_payment)}
                                </span>
                              </span>
                            : dealState.is_one_pay
                                // One-pay lease: fixed value in the bordered box.
                                ? <div className={'monthly-val-wrap'}>
                                    <span className={'dollar'}>$</span>
                                    <span className={'amount payment-val-blue-text'}>
                                        {formatNumber(formattedQuote.monthly_payment)}
                                    </span>
                                  </div>
                                // Lease: opens Payment Adjustment (the payment is typed there).
                                // (Handlers sit on the wrap: NumericFormat defers its own onFocus.)
                                : <div
                                    className={'monthly-val-wrap monthly-val-editable'}
                                    ref={paymentFieldRef}
                                    onClick={openPaymentAdjustment}
                                    onFocus={openPaymentAdjustment}
                                  >
                                    <span className={'dollar'}>$</span>
                                    <CustomCurrencyInput
                                        className={'monthly-payment-input'}
                                        value={formattedQuote.monthly_payment}
                                        disabled={isHistorical}
                                        inputProps={{readOnly: true, 'aria-haspopup': 'dialog', style: {cursor: 'pointer'}}}
                                        InputProps={{}}
                                      />
                                  </div>
                    }
                </div>
            </div>
            {
                adjustAnchor &&
                <PaymentAdjustmentPopover
                    anchorEl={adjustAnchor}
                    currentPayment={formattedQuote.monthly_payment}
                    cost={dealState.cost}
                    profit={dealState.profit}
                    totalDriveOff={formattedQuote.amount_due_at_signing}
                    onConfirm={(payment, choice) => {
                        submitPaymentAdjustment(payment, choice)
                        setAdjustAnchor(null)
                    }}
                    onClose={() => setAdjustAnchor(null)}
                />
            }
            {
                breakdownAnchor &&
                <AmountDueBreakdownPopover
                    anchorEl={breakdownAnchor}
                    dealId={dealState.id}
                    onClose={() => setBreakdownAnchor(null)}
                />
            }
        </Box>
    )
}
